import React, { useEffect, useRef, useState } from "react";
import { getFirestore, collection, query, where, onSnapshot, updateDoc, doc, getDocs } from "firebase/firestore";
import { FiSearch, FiShield, FiShieldOff, FiChevronRight } from "react-icons/fi";

// ── Avatar ───────────────────────────────────────────────────────
function Avatar({ name, size = 36 }) {
    const initials = (name || "?").split(" ").map(w => w[0] || "").join("").slice(0, 2).toUpperCase();
    return (
        <div style={{
            width: size, height: size, borderRadius: "50%", flexShrink: 0,
            background: "#3a82c6", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: size * 0.36, fontWeight: 700,
            fontFamily: "'Quicksand', sans-serif",
        }}>
            {initials}
        </div>
    );
}

// ── Action button ────────────────────────────────────────────────
function ActionBtn({ icon, label, color, border, onClick, disabled }) {
    const ref = useRef(null);
    return (
        <button
            ref={ref}
            onClick={onClick}
            disabled={disabled}
            onMouseDown={() => { if (ref.current && !disabled) { ref.current.style.transform = "translateY(2px)"; ref.current.style.borderBottomWidth = "2px"; } }}
            onMouseUp={() => { if (ref.current) { ref.current.style.transform = ""; ref.current.style.borderBottomWidth = "3px"; } }}
            onMouseLeave={() => { if (ref.current) { ref.current.style.transform = ""; ref.current.style.borderBottomWidth = "3px"; } }}
            style={{
                height: 32, borderRadius: 9, padding: "0 12px",
                border: `1.5px solid ${border}`, borderBottomWidth: 3,
                background: disabled ? "#eee" : color, color: "#fff",
                display: "inline-flex", alignItems: "center", gap: 5,
                cursor: disabled ? "not-allowed" : "pointer",
                fontFamily: "'Quicksand', sans-serif", fontWeight: 700, fontSize: 12,
                transition: "transform 80ms ease, border-bottom-width 80ms ease",
                opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap",
            }}
        >
            {icon}{label}
        </button>
    );
}

export default function AdminUsers() {
    const db = getFirestore();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");

    // Promote panel
    const [allUsers, setAllUsers] = useState([]);
    const [promoteSearch, setPromoteSearch] = useState("");
    const [promoteLoading, setPromoteLoading] = useState(false);
    const [promoteError, setPromoteError] = useState("");

    // Live admin list
    useEffect(() => {
        setLoading(true);
        const q = query(collection(db, "users"), where("admin", "==", true));
        const unsub = onSnapshot(
            q,
            (snap) => {
                const rows = snap.docs.map(d => {
                    const data = d.data() || {};
                    return {
                        id: d.id,
                        name: data.displayName || data.name || "",
                        email: data.email || "",
                        verified: !!data.verified,
                    };
                });
                rows.sort((a, b) => (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase()));
                setAdmins(rows);
                setLoading(false);
            },
            () => {
                setError("Could not load admin users. Check your Firestore rules.");
                setLoading(false);
            }
        );
        return () => unsub();
    }, [db]);

    // Fetch all non-admin users for promotion
    useEffect(() => {
        const fetch = async () => {
            try {
                const snap = await getDocs(collection(db, "users"));
                const users = snap.docs.map(d => {
                    const data = d.data() || {};
                    return {
                        id: d.id,
                        name: data.displayName || data.name || "",
                        email: data.email || "",
                        admin: !!data.admin,
                    };
                });
                setAllUsers(users.filter(u => !u.admin));
            } catch {
                setPromoteError("Could not load users.");
            }
        };
        fetch();
    }, [admins, db]);

    const handleRemove = async (id) => {
        const target = admins.find(u => u.id === id);
        if (!window.confirm(`Remove admin from ${target?.name || "this user"}?`)) return;
        setPromoteLoading(true);
        try {
            await updateDoc(doc(db, "users", id), { admin: false });
        } catch {
            setPromoteError("Failed to remove admin. Check permissions.");
        } finally {
            setPromoteLoading(false);
        }
    };

    const handlePromote = async (user) => {
        setPromoteLoading(true);
        setPromoteError("");
        try {
            await updateDoc(doc(db, "users", user.id), { admin: true });
            setPromoteSearch("");
        } catch {
            setPromoteError("Failed to promote user. Check permissions.");
        } finally {
            setPromoteLoading(false);
        }
    };

    const filteredAdmins = admins.filter(u => {
        const q = search.toLowerCase().trim();
        if (!q) return true;
        return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    });

    const promoteResults = promoteSearch.trim().length > 0
        ? allUsers.filter(u =>
            (u.name || "").toLowerCase().includes(promoteSearch.toLowerCase()) ||
            (u.email || "").toLowerCase().includes(promoteSearch.toLowerCase())
          ).slice(0, 8)
        : [];

    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", gap: 12, color: "#606060", fontWeight: 600, fontFamily: "'Quicksand', sans-serif" }}>
                <div style={{ width: 20, height: 20, border: "2.5px solid #E0E0E0", borderTop: "2.5px solid #3a82c6", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                Loading admin users…
            </div>
        );
    }

    return (
        <div style={{ fontFamily: "'Quicksand', sans-serif", maxWidth: 860 }}>

            {/* ── Page header ── */}
            <div style={{ marginBottom: 28 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <h1 style={{ margin: 0 }}>Admin Users</h1>
                    <span style={{
                        background: "#3a82c6", color: "#fff",
                        borderRadius: 999, padding: "3px 14px",
                        fontSize: 13, fontWeight: 700,
                    }}>
                        {admins.length}
                    </span>
                </div>
                <div style={{ color: "#606060", fontWeight: 600, fontSize: 14, marginTop: 4 }}>
                    Users with CMS access. Changes take effect immediately.
                </div>
            </div>

            {error && (
                <div style={{
                    background: "#fde8e8", border: "1px solid #f5c6c6", borderRadius: 12,
                    padding: "12px 16px", color: "#c0392b", fontWeight: 600, fontSize: 14, marginBottom: 20,
                }}>
                    {error}
                </div>
            )}

            {/* ── Current admins ── */}
            <div style={{
                background: "#fff", borderRadius: 20, border: "1px solid #E0E0E0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden", marginBottom: 24,
            }}>
                {/* Card header with search */}
                <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "16px 20px", borderBottom: "1px solid #E0E0E0", gap: 12, flexWrap: "wrap",
                }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>Current Admins</span>
                    <div style={{ position: "relative", flex: "1 1 220px", maxWidth: 320 }}>
                        <FiSearch size={13} style={{
                            position: "absolute", left: 10, top: "50%",
                            transform: "translateY(-50%)", color: "#aaa", pointerEvents: "none",
                        }} />
                        <input
                            type="text"
                            placeholder="Search by name or email…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{
                                fontFamily: "'Quicksand', sans-serif", fontSize: 13, fontWeight: 600,
                                padding: "8px 12px 8px 30px", borderRadius: 10,
                                border: "1.5px solid #E0E0E0", background: "#f7f9fb",
                                width: "100%", outline: "none", color: "#000", boxSizing: "border-box",
                            }}
                        />
                    </div>
                </div>

                {filteredAdmins.length === 0 ? (
                    <div style={{ padding: "36px 20px", textAlign: "center", color: "#aaa", fontWeight: 600, fontSize: 14 }}>
                        {search ? "No admins match your search." : "No admin users found."}
                    </div>
                ) : (
                    filteredAdmins.map((u, idx) => {
                        const isLast = idx === filteredAdmins.length - 1;
                        return (
                            <div
                                key={u.id}
                                style={{
                                    display: "flex", alignItems: "center", gap: 14,
                                    padding: "13px 20px",
                                    borderBottom: isLast ? "none" : "1px solid #f3f3f3",
                                    background: idx % 2 === 0 ? "#fff" : "#f7f9fb",
                                }}
                            >
                                <Avatar name={u.name} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {u.name || "Unnamed"}
                                    </div>
                                    <div style={{ fontSize: 12, color: "#606060", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {u.email || "—"}
                                    </div>
                                </div>
                                <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                                    <span style={{
                                        background: "#3a82c618", color: "#1565c0",
                                        border: "1px solid #3a82c640", borderRadius: 999,
                                        fontSize: 11, fontWeight: 700, padding: "2px 9px",
                                    }}>
                                        Admin
                                    </span>
                                    {u.verified && (
                                        <span style={{
                                            background: "#50C87818", color: "#27774a",
                                            border: "1px solid #50C87840", borderRadius: 999,
                                            fontSize: 11, fontWeight: 700, padding: "2px 9px",
                                        }}>
                                            ✓ Verified
                                        </span>
                                    )}
                                </div>
                                <ActionBtn
                                    icon={<FiShieldOff size={13} />}
                                    label="Remove"
                                    color="#e63946" border="#b82d38"
                                    onClick={() => handleRemove(u.id)}
                                    disabled={promoteLoading}
                                />
                            </div>
                        );
                    })
                )}
            </div>

            {/* ── Promote panel ── */}
            <div style={{
                background: "#fff", borderRadius: 20, border: "1px solid #E0E0E0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "visible",
            }}>
                <div style={{ padding: "16px 20px", borderBottom: "1px solid #E0E0E0" }}>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Promote User to Admin</div>
                    <div style={{ fontSize: 12, color: "#606060", fontWeight: 600 }}>
                        Search by name or email, then click Promote.
                    </div>
                </div>

                <div style={{ padding: "16px 20px" }}>
                    {promoteError && (
                        <div style={{
                            background: "#fde8e8", border: "1px solid #f5c6c6", borderRadius: 10,
                            padding: "10px 14px", color: "#c0392b", fontWeight: 600, fontSize: 13, marginBottom: 12,
                        }}>
                            {promoteError}
                        </div>
                    )}

                    <div style={{ position: "relative" }}>
                        <div style={{ position: "relative" }}>
                            <FiSearch size={13} style={{
                                position: "absolute", left: 12, top: "50%",
                                transform: "translateY(-50%)", color: "#aaa", pointerEvents: "none", zIndex: 1,
                            }} />
                            <input
                                type="text"
                                placeholder="Search users to promote…"
                                value={promoteSearch}
                                onChange={e => setPromoteSearch(e.target.value)}
                                style={{
                                    fontFamily: "'Quicksand', sans-serif", fontSize: 14, fontWeight: 600,
                                    padding: "11px 14px 11px 34px", borderRadius: 12,
                                    border: "1.5px solid #E0E0E0", background: "#f7f9fb",
                                    width: "100%", outline: "none", color: "#000", boxSizing: "border-box",
                                }}
                                onFocus={e => { e.target.style.borderColor = "#2a9d8f"; }}
                                onBlur={e => { e.target.style.borderColor = "#E0E0E0"; }}
                            />
                        </div>

                        {/* Results dropdown */}
                        {promoteSearch.trim() && (
                            <div style={{
                                position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
                                background: "#fff", borderRadius: 14,
                                border: "1.5px solid #E0E0E0",
                                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                zIndex: 100, overflow: "hidden",
                            }}>
                                {promoteResults.length === 0 ? (
                                    <div style={{
                                        padding: "16px 18px", color: "#aaa",
                                        fontWeight: 600, fontSize: 13, textAlign: "center",
                                    }}>
                                        No users found for "{promoteSearch}"
                                    </div>
                                ) : (
                                    promoteResults.map((u, idx) => {
                                        const isLast = idx === promoteResults.length - 1;
                                        return (
                                            <div
                                                key={u.id}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 12,
                                                    padding: "11px 16px",
                                                    borderBottom: isLast ? "none" : "1px solid #f3f3f3",
                                                    background: "#fff",
                                                    transition: "background 0.12s",
                                                }}
                                                onMouseEnter={e => { e.currentTarget.style.background = "#f7f9fb"; }}
                                                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
                                            >
                                                <div style={{
                                                    width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                                                    background: "#2a9d8f18", color: "#2a9d8f",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    fontSize: 12, fontWeight: 700,
                                                }}>
                                                    {(u.name || "?").split(" ").map(w => w[0] || "").join("").slice(0, 2).toUpperCase()}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {u.name || "Unnamed"}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: "#606060", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {u.email || "No email"}
                                                    </div>
                                                </div>
                                                <ActionBtn
                                                    icon={<FiShield size={13} />}
                                                    label="Promote"
                                                    color="#2a9d8f" border="#258D80"
                                                    onClick={() => handlePromote(u)}
                                                    disabled={promoteLoading}
                                                />
                                                <FiChevronRight size={14} style={{ color: "#ccc", flexShrink: 0 }} />
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>

                    {!promoteSearch.trim() && (
                        <div style={{
                            marginTop: 12, fontSize: 12, color: "#aaa", fontWeight: 600,
                            textAlign: "center",
                        }}>
                            {allUsers.length} user{allUsers.length !== 1 ? "s" : ""} eligible for promotion
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
